<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" :title="`${$route.params.id ? 'Edit' : 'Create'} Booking Form`" :back-route="{ name: 'BookingFormList' }">
      <button v-if="$route.params.id" class="btn btn-secondary" @click="showLinkModal = true">
        <fa :icon="['far', 'link']" class="mr-2" /> Get Link
      </button>
    </PageHeader>
    
    <div class="container">
      <Loading v-if="$route.params.id && loading" />
      <form @submit.prevent="upsertForm" v-else>
        
        <div class="grid space-y-4 lg:space-y-0 lg:grid-cols-3 lg:gap-4">
          <div class="space-y-4 lg:col-span-2">
            <div class="card">
              <div class="card-header">
                Details
              </div>
              <div class="p-6">
                <fieldset class="form-group">
                  <label for="name" class="form-label">Form Name</label>
                  <input id="name" name="name" type="text" class="form-input" v-model="form.name">
                  <validations :errors="errors" field="name" />
                </fieldset>

                <!-- <fieldset class="form-group">
                  <label for="name" class="form-label">Booking Type</label>

                  <radio-grid
                    class="mt-3 lg:grid-cols-2"
                    v-model="form.type"
                    name="type"
                    :options="[
                      { 
                        label: `Transport`, 
                        description: 'A transport form includes both pickup/collection and dropoff/delivery address options. Perfect for couriers and taxi services.',
                        value: 'transport' 
                      },
                      { 
                        label: `Pickup`, 
                        description: 'A pickup form includes only a pickup/collection address option. Great for junk, garbage or recycling collection.',
                        value: 'pickup' 
                      },
                      { 
                        label: `Delivery`, 
                        description: 'A delivery form includes only a dropoff/delivery address option. Works well for restaurants, retailers or rental companies.',
                        value: 'delivery' 
                      },
                      { 
                        label: `Service`, 
                        description: 'A service form includes an option to enter an address for where a service will be performed. Great for maintenance and/or installation businesses.',
                        value: 'service' 
                      }
                    ]" />
                  <validations :errors="errors" field="type" />
                </fieldset> -->
              </div>
            </div>

            <template v-if="form.type === 'pickup' || form.type === 'transport'">
              <div class="card">
                <div class="card-header">
                  Pickups / Collections <span class="font-normal text-gray-500">(Origins)</span>
                </div>
                <div class="p-6">
                  <fieldset class="form-group">
                    <alert v-if="form.allow_multiple_destinations" class="mb-4" icon="info">
                      You cannot enable multiple origins and multiple destinations on the same form. To enable multiple origins, you'll need to disable multiple destinations (below) first.
                    </alert>
                    <label class="flex items-start form-label" :class="{ 'opacity-50': form.allow_multiple_destinations }">
                      <input type="checkbox" class="mr-2 text-xl form-checkbox" v-model="form.allow_multiple_origins" :disabled="form.allow_multiple_destinations">
                      <div>
                        Allow Multiple Origins
                        <div class="text-sm font-normal text-gray-500">
                          Gives customers the option of adding more than one pickup address. 
                        </div>
                      </div>
                    </label>
                  </fieldset>
                  <fieldset class="form-group">
                    <label class="flex items-start form-label">
                      Pickup Help Text
                    </label>
                    <input type="text" class="form-input" v-model="form.origin_help_text">
                    <div class="mt-1 text-sm leading-tight text-gray-500">
                      This text will appear on the pickup address entry step. You may enter a up to 255 characters.
                    </div>
                  </fieldset>
                </div>
              </div>
            </template>

            <template v-if="form.type !== 'pickup'">
              <div class="card">
                <div class="card-header">
                  Deliveries / Dropoffs <span class="font-normal text-gray-500">(Destinations)</span>
                </div>
                <div class="p-6">
                  <fieldset class="form-group">
                    <alert v-if="form.allow_multiple_origins" class="mb-4" icon="info">
                      You cannot enable multiple destinations and multiple origins on the same form. To enable multiple origins, you'll need to disable multiple origins (above) first.
                    </alert>
                    <label class="flex items-start form-label" :class="{ 'opacity-50': form.allow_multiple_origins }">
                      <input type="checkbox" class="mr-2 text-xl form-checkbox" v-model="form.allow_multiple_destinations" :disabled="form.allow_multiple_origins">
                      <div>
                        Allow Multiple Destinations
                        <div class="text-sm font-normal text-gray-500">
                          Gives customers the option of adding more than one delivery address. 
                        </div>
                      </div>
                    </label>
                  </fieldset>
                  <fieldset class="form-group">
                    <label class="flex items-start form-label">
                      Delivery Help Text
                    </label>
                    <input type="text" class="form-input" v-model="form.destination_help_text">
                    <div class="mt-1 text-sm leading-tight text-gray-500">
                      This text will appear on the delivery address entry step. You may enter a up to 255 characters.
                    </div>
                  </fieldset>
                </div>
              </div>
            </template>

            <div class="card">
              <div class="card-header">
                Date
              </div>
              <div class="p-6">
                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    Date Requirement
                  </label>
                  <radio-stack
                    v-model="form.date_requirement"
                    name="date_requirement"
                    :options="[
                      { 
                        label: `Don't Ask`, 
                        description: 'No date step will be shown.',
                        value: null 
                      },
                      { 
                        label: `Optional`, 
                        description: 'A date step will be shown but may be skipped.',
                        value: 'optional' 
                      },
                      { 
                        label: `Required`, 
                        description: 'A date step will be shown and a date must be selecte to progress to the next step.',
                        value: 'required' 
                      }
                    ]" />
                </fieldset>
                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    Date Help Text
                  </label>
                  <input type="text" class="form-input" v-model="form.date_help_text">
                  <div class="mt-1 text-sm leading-tight text-gray-500">
                    This text will appear on the date select step. You may enter a up to 255 characters.
                  </div>
                </fieldset>
              </div>
            </div>
            
            <div class="card">
              <div class="card-header">
                Time
              </div>
              <div class="p-6">
                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    Time Requirement
                  </label>
                  <radio-stack
                    v-model="form.time_requirement"
                    name="time_requirement"
                    :options="[
                      { 
                        label: `Don't Ask`, 
                        description: 'No time step will be shown.', 
                        value: null 
                      },
                      { 
                        label: `Optional`, 
                        description: 'A time step will be shown but may be skipped.', 
                        value: 'optional' 
                      },
                      { 
                        label: `Required`, 
                        description: 'A time step will be shown and a time must be selected to progress to the next step.', 
                        value: 'required' 
                      }
                    ]" />
                </fieldset>
                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    Time Help Text
                  </label>
                  <input type="text" class="form-input" v-model="form.time_help_text">
                  <div class="mt-1 text-sm leading-tight text-gray-500">
                    This text will appear on the time select step. You may enter a up to 255 characters.
                  </div>
                </fieldset>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                Schedule Rules
              </div>
              <div class="p-6">
                <fieldset class="grid gap-4 pb-4 form-group md:grid-cols-2">
                  <div>
                    <label class="flex items-start form-label">
                      Maximum Lead Time
                    </label>
                    <div class="mt-1 text-sm leading-tight text-gray-500">
                      Customers will not be able to choose a date more than this many days in the future from their time of booking.
                    </div>
                  </div>
                  <div class="relative h-10 rounded-md">
                    <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span class="text-gray-400">
                        days
                      </span>
                    </div>
                    <input type="text" inputmode="numeric" class="form-input" v-model="form.maximum_lead_time">
                  </div>
                </fieldset>

                <fieldset class="grid gap-4 pb-4 form-group md:grid-cols-2">
                  <div>
                    <label class="flex items-start form-label">
                      Minimum Lead Time
                    </label>
                    <div class="mt-1 text-sm leading-tight text-gray-500">
                      Customers will not be able to select dates and times that are not at least this many hours away from their time of booking.
                    </div>
                  </div>
                  <div class="relative h-10 rounded-md">
                    <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span class="text-gray-400">
                        hours
                      </span>
                    </div>
                    <input type="text" inputmode="numeric" class="form-input" v-model="form.minimum_lead_time">
                  </div>  
                </fieldset>
                
                <fieldset class="grid gap-4 pb-4 form-group md:grid-cols-2">
                  <div>
                    <label class="flex items-start form-label">
                      Timeslot Intervals
                    </label>
                    <div class="mt-1 text-sm leading-tight text-gray-500">
                      Choose the number of minutes between available timeslots on the Time selection step.
                    </div>
                  </div>
                  <div class="relative h-10 rounded-md">
                    <select class="form-select" v-model="form.timeslot_interval">
                      <option value="10">10 minutes</option>
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                    </select>
                  </div>
                </fieldset>

                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    Operational Times
                  </label>
                  <div class="mt-1 text-sm leading-tight text-gray-500">
                    Set time schedules for each day of the week:
                  </div>
                  <schedule-toggle 
                    class="mt-2"
                    :parent="form" 
                    @toggle="toggleSchedule" />
                </fieldset>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                Products &amp; Addons
              </div>
              <div class="p-6">
                <fieldset class="form-group">
                  <div class="flex items-center justify-between">
                    <div class="leading-tight">
                      <label class="flex items-start mb-0 form-label">
                        Products
                      </label>
                      <div class="text-sm leading-tight text-gray-500">
                        Select the products available on this form:
                      </div>
                    </div>
                    <div>
                      <button type="button" class="btn btn-sm btn-secondary" @click="showEditProductsModal = true">
                        Edit Products
                      </button>
                    </div>
                  </div>
                  <Modal title="Edit Products" :open.sync="showEditProductsModal">
                    <ProductToggle 
                      :parent="form" 
                      class="mt-2"
                      @toggle="toggleProduct" />
                  </Modal>
                  <ProductSort :products.sync="form.products" class="mt-4" />
                </fieldset>
                <fieldset class="mt-6 form-group">
                  <div class="flex items-center justify-between">
                    <div class="leading-tight">
                      <label class="flex items-start mb-0 form-label">
                        Addons
                      </label>
                      <div class="text-sm leading-tight text-gray-500">
                        Select the addons available on this form:
                      </div>
                    </div>
                    <div>
                      <button type="button" class="btn btn-sm btn-secondary" @click="showEditAddonsModal = true">
                        Edit Addons
                      </button>
                    </div>
                  </div>
                  <Modal title="Edit Products" :open.sync="showEditAddonsModal">
                    <AddonToggle 
                      :parent="form" 
                      class="mt-2"
                      @toggle="toggleAddon" />
                  </Modal>
                  <AddonSort :addons.sync="form.addons" class="mt-4" />
                </fieldset>
              </div>
            </div>
          </div>

          <div class="space-y-4 lg:col-span-1">
            <div class="card">
              <div class="card-header">
                Portal Options
              </div>
              <div class="p-6">
                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    <input type="checkbox" class="mr-2 text-xl form-checkbox" v-model="form.show_on_portal">
                    <div>
                      Show On Customer Portal
                      <div class="text-sm font-normal text-gray-500">
                        If this box is checked, this form will be accessible from a "New Booking" button on your Customer Portal. If you have more than one booking form shown on the customer portal, customers will need to choose between each form by its Name, which you can change above.
                      </div>
                    </div>
                  </label>
                </fieldset>
              </div>
            </div>
            <div class="card">
              <div class="card-header">
                Confirmation &amp; Legal
              </div>
              <div class="p-6">
                <fieldset class="form-group">
                  <label class="flex items-start form-label">
                    <input type="checkbox" class="mr-2 text-xl form-checkbox" v-model="form.require_confirmation">
                    <div>
                      Require Confirmation
                      <div class="text-sm font-normal text-gray-500">
                        This will display a checkbox at the end of the form, that must be checked in order to send the order.
                      </div>
                    </div>
                  </label>
                </fieldset>
                <fieldset class="form-group" v-show="form.require_confirmation">
                  <label for="" class="form-label">Confirmation Text</label>
                  <textarea type="text" class="form-input" v-model="form.confirmation_text"></textarea>
                  <validations :errors="errors" field="confirmation_text" />
                </fieldset>
                <fieldset class="form-group">
                  <label for="" class="form-label">Terms &amp; Conditions</label>
                  <textarea type="text" class="form-input" v-model="form.legal_terms"></textarea>
                  <validations :errors="errors" field="legal_terms" />
                </fieldset>
                <fieldset class="form-group">
                  <label for="" class="form-label">Privacy Policy</label>
                  <textarea type="text" class="form-input" v-model="form.legal_privacy"></textarea>
                  <validations :errors="errors" field="legal_privacy" />
                </fieldset>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-between py-6">
          <button class="btn btn-primary" v-submitting="submitting">
            {{ $route.params.id ? 'Save' : 'Create' }} Form
          </button>
          <button v-if="$route.params.id" type="button" class="font-medium text-red-500 hover:underline" @click="deleteForm()" v-submitting="submitting">
            Delete Form
          </button>
        </div>
      </form>
    </div>

    <Modal title="Link To This Form" :open="showLinkModal" @close="showLinkModal = false">
      <div class="p-6">
        <fieldset class="form-group">
          <label class="form-label">URL</label>
          <div class="flex">
            <input type="url" readonly disabled class="form-input prepend" :value="url">
            <button class="rounded-l-none btn btn-secondary"
              v-clipboard:copy="url"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError">
              <fa :icon="['far', 'copy']" class="mr-2" /> 
              Copy
            </button>
          </div>
          <div class="mt-1 text-sm text-gray-500">
            Copy and send this link to customers, or link to it from your website.
          </div>
        </fieldset>
      </div>
    </Modal>
  </div>
</template>

<script>
  import AddonSort from '@/components/AddonSort'
  import AddonToggle from '@/components/AddonToggle'
  import ProductSort from '@/components/ProductSort'
  import ProductToggle from '@/components/ProductToggle'

  export default {
    name: 'BookingFormForm',

    components: {
      AddonSort,
      AddonToggle,
      ProductSort,
      ProductToggle
    },

    created () {
      if (this.$route.params.id) {
        this.getForm()
      }
    },

    computed: {
      url () {
        if (process.env.NODE_ENV === 'development') {
          return `http://localhost:9090/portal/${this.$route.params.account}/book/${this.form.uuid}`
        } else {
          return `https://${this.$route.params.account}.flt.onl/book/${this.form.uuid}`
        }
      }
    },

    data () {
      return {
        showLinkModal: false,
        showEditProductsModal: false,
        showEditAddonsModal: false,
        submitting: false,
        loading: false,
        errors: [],
        form_errors: [],
        form: {
          name: '',
          type: 'transport',

          allow_multiple_origins: false,
          minimum_origins: null,
          maximum_origins: null,
          origin_help_text: '',

          allow_multiple_destinations: false,
          minimum_destinations: null,
          maximum_destinations: null,
          destination_help_text: '',
          
          date_requirement: 'optional',
          date_help_text: '',

          time_requirement: 'optional',
          time_help_text: '',

          maximum_lead_time: 365,
          minimum_lead_time: 0,
          timeslot_interval: 15,
          
          show_on_portal: false,

          require_confirmation: false,
          confirmation_text: 'I confirm that all the details I have entered here are correct.',

          legal_terms: '',
          legal_privacy: '',

          schedules: [],
          
          products: [],
          addons: [],

        }
      }
    },

    methods: {
      async getForm () {
        try {
          const { data } = await this.$api.get(`account/${this.$route.params.account}/booking-forms/${this.$route.params.id}`)
          this.form = data.form
          this.loading = false
        } catch (error) {
          console.error(error)
        }
      },

      async upsertForm () {
        try {
          this.submitting = true
          this.updateSortIndices()
          if (this.$route.params.id) {
            await this.$api.patch(`account/${this.$route.params.account}/booking-forms/${this.$route.params.id}`, this.form)
          } else {
            await this.$api.post(`account/${this.$route.params.account}/booking-forms`, this.form)
          }
          this.$router.push({ name: 'BookingFormList' })
        } catch (error) {
          console.error(error)
          this.errors = error.response.data.errors ? error.response.data.errors : []
        } finally {
          this.submitting = false
        }
      },

      async deleteForm () {
        if (window.confirm('Are you sure you want to delete this form?')) {
          try {
            this.submitting = true
            await this.$api.delete(`account/${this.$route.params.account}/booking-forms/${this.$route.params.id}`)
            this.$router.push({ name: 'BookingFormList' })
          } catch (error) {
            console.error(error)
          } finally {
            this.submitting = false
          }
        }
      },

      toggleSchedule (schedule) {
        let index = this.form.schedules.findIndex(s => s.id === schedule.id)

        if (index > -1) {
          this.$delete(this.form.schedules, index)
        } else {
          this.$set(this.form.schedules, this.form.schedules.length, schedule)
        }
      },

      toggleProduct (product) {
        let index = this.form.products.findIndex(s => s.id === product.id)

        if (index > -1) {
          this.$delete(this.form.products, index)
        } else {
          this.$set(this.form.products, this.form.products.length, product)
        }
      },

      toggleAddon (addon) {
        let index = this.form.addons.findIndex(s => s.id === addon.id)

        if (index > -1) {
          this.$delete(this.form.addons, index)
        } else {
          this.$set(this.form.addons, this.form.addons.length, addon)
        }
      },

      updateSortIndices () {
        this.form.products.forEach((product, index) => { product.sort_index = index })
        this.form.addons.forEach((addon, index) => { addon.sort_index = index })
      },

      onCopy (e) {
        this.$toasted.show('Copied URL to clipboard.')
      },

      onError (e) {
        this.$toasted.error('Failed to copy URL. Try again.')
      }
    }
  }
</script>